import React from 'react';
import { Button, Checkbox, Form, Header, Icon, Input, Message, Segment } from 'semantic-ui-react';
import FIELD_TYPE_OPTIONS from 'shared/constants/FIELD_TYPE_OPTIONS';
import { FIELD } from 'shared/constants/FIELD_TYPE';
import TierLabel from '../subcomponents/TierLabel';
import { RECOMMENDATION_TIERS } from '../constants';

// A small, common subset keeps setup quick; every other type is available later in Data Customization
const WIZARD_FIELD_TYPES = [FIELD.TEXT, FIELD.NUMBER, FIELD.CURRENCY, FIELD.DATE, FIELD.DROPDOWN];
const FIELD_TYPE_CHOICES = FIELD_TYPE_OPTIONS.filter(({ value }) => WIZARD_FIELD_TYPES.includes(value));

const CustomFields = ({ fieldGroup, errors, explanation, onChange, onBack, onContinue, canContinue }) => {
    const { name, fields } = fieldGroup;

    const updateFields = (newFields) => onChange({ ...fieldGroup, fields: newFields });
    const updateField = (id, changes) => updateFields(fields.map(field => field.id === id ? { ...field, ...changes } : field));
    const addField = () => updateFields([...fields, { id: `custom_${Date.now()}`, label: '', field_type: FIELD.TEXT, options: [], selected: true, is_custom: true }]);

    const selectedCount = fields.filter(({ selected }) => selected).length;

    return (
        <Segment>
            <Header as='h3'>
                Contact Fields <TierLabel tier={RECOMMENDATION_TIERS.RECOMMENDED} />
                <Header.Subheader>{explanation} These are added as a custom field group on contacts.</Header.Subheader>
            </Header>
            <Form noValidate>
                <Form.Field width={8} error={!!errors.name}>
                    <label>Field Group Name</label>
                    <Input value={name} onChange={(_, { value }) => onChange({ ...fieldGroup, name: value })} />
                    {errors.name && <div className='Onboarding__field-error'>{errors.name}</div>}
                </Form.Field>
                {fields.map(field => {
                    const fieldErrors = errors.fields?.[field.id] || {};
                    return (
                        <Segment key={field.id} className={`Onboarding__recommendation ${field.selected ? 'Onboarding__recommendation--selected' : ''}`}>
                            <div className='Onboarding__field-row'>
                                <Checkbox checked={field.selected} onChange={() => updateField(field.id, { selected: !field.selected })} aria-label={`Include ${field.label}`} />
                                <Form.Field error={!!fieldErrors.label} className='Onboarding__field-label'>
                                    <Input size='small' placeholder='Field label' value={field.label} disabled={!field.selected} onChange={(_, { value }) => updateField(field.id, { label: value })} />
                                    {fieldErrors.label && <div className='Onboarding__field-error'>{fieldErrors.label}</div>}
                                </Form.Field>
                                <Form.Select
                                    className='Onboarding__field-type'
                                    size='small'
                                    value={field.field_type}
                                    options={FIELD_TYPE_CHOICES}
                                    disabled={!field.selected}
                                    onChange={(_, { value }) => updateField(field.id, { field_type: value })}
                                />
                                {field.is_custom && <Icon name='trash alternate outline' link color='red' onClick={() => updateFields(fields.filter(({ id }) => id !== field.id))} aria-label='Remove field' />}
                            </div>
                            {field.field_type === FIELD.DROPDOWN && field.selected && (
                                <Form.Field error={!!fieldErrors.options} className='Onboarding__field-options'>
                                    <Input
                                        size='small'
                                        fluid
                                        label='Options'
                                        placeholder='Separate options with commas'
                                        value={field.options_text ?? field.options.join(', ')}
                                        onChange={(_, { value }) => updateField(field.id, {
                                            options_text: value,
                                            options: value.split(',').map(option => option.trim()).filter(Boolean)
                                        })}
                                    />
                                    {fieldErrors.options && <div className='Onboarding__field-error'>{fieldErrors.options}</div>}
                                </Form.Field>
                            )}
                        </Segment>
                    );
                })}
            </Form>
            <Button basic size='small' type='button' onClick={addField}><Icon name='plus' /> Add Field</Button>
            {!selectedCount && (
                <Message info size='small'>No custom fields will be added. Contacts still have the standard fields (name, phone, email, address).</Message>
            )}
            <div className='Onboarding__footer'>
                <Button basic type='button' onClick={onBack}><Icon name='arrow left' /> Back</Button>
                <Button primary floated='right' type='button' disabled={!canContinue} onClick={onContinue}>
                    Review CRM Setup <Icon name='arrow right' />
                </Button>
                <div style={{ clear: 'both' }} />
            </div>
        </Segment>
    );
}

export default CustomFields;
